'use client';

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Star, BookOpen, Sparkles, TrendingUp, Users, Compass, Brain, Calendar, Zap } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function TarotInterpretationTechniquesPage() {
  const beginnerSpreads = [
    {
      name: "Tek Kart Çekimi",
      difficulty: "Başlangıç",
      cards: 1,
      description: "En basit yöntem. Günlük rehberlik, hızlı içgörü veya net cevap için.",
      uses: ["Günlük mesaj", "Hızlı karar", "Durum analizi"],
      icon: <Star className="h-6 w-6" />,
      path: "tek-kart"
    },
    {
      name: "Üç Kart Yayılımı",
      difficulty: "Başlangıç",
      cards: 3,
      description: "En popüler ve çok yönlü! Farklı şekillerde yorumlanabilir.",
      uses: ["Geçmiş-Şimdi-Gelecek", "Durum-Eylem-Sonuç", "Zihin-Beden-Ruh"],
      icon: <BookOpen className="h-6 w-6" />,
      path: "uc-kart"
    }
  ];

  const intermediateSpreads = [
    {
      name: "Celtic Cross (Kelt Haçı)",
      difficulty: "Orta",
      cards: 10,
      description: "En ünlü ve kapsamlı spread. Durumun tüm yönlerini inceler.",
      uses: ["Kapsamlı analiz", "Karmaşık durumlar", "Detaylı gelecek"],
      icon: <Compass className="h-6 w-6" />,
      path: "celtic-cross"
    },
    {
      name: "İlişki Yayılımı",
      difficulty: "Orta",
      cards: 7,
      description: "İki kişi veya durum arasındaki dinamiği gösterir.",
      uses: ["Aşk ilişkileri", "İş ortaklıkları", "Aile dinamikleri"],
      icon: <Users className="h-6 w-6" />,
      path: "iliski-yayilimi"
    },
    {
      name: "Karar Verme Yayılımı",
      difficulty: "Orta",
      cards: 6,
      description: "İki seçenek arasında karşılaştırma yapar.",
      uses: ["Önemli kararlar", "Seçenek analizi", "Risk değerlendirme"],
      icon: <TrendingUp className="h-6 w-6" />,
      path: "karar-verme"
    }
  ];

  const advancedSpreads = [
    {
      name: "Astrolojik Yayılım",
      difficulty: "İleri",
      cards: 12,
      description: "12 astroloji evini temsil eder. Hayatın tüm alanlarını kapsar.",
      uses: ["Yıllık okuma", "Detaylı analiz", "Hayat değerlendirmesi"],
      icon: <Sparkles className="h-6 w-6" />,
      path: "astrolojik-yayilim"
    },
    {
      name: "Zaman Çizelgesi",
      difficulty: "İleri",
      cards: 12,
      description: "Haftalık, aylık veya yıllık projeksiyonlar.",
      uses: ["Gelecek planlaması", "Zaman dilimleri", "Döngüsel analiz"],
      icon: <Calendar className="h-6 w-6" />,
      path: "zaman-cizelgesi"
    },
    {
      name: "Chakra Yayılımı",
      difficulty: "İleri",
      cards: 7,
      description: "7 chakra enerjisini değerlendirir.",
      uses: ["Ruhsal sağlık", "Enerji dengesi", "Kişisel gelişim"],
      icon: <Zap className="h-6 w-6" />,
      path: "chakra-yayilimi"
    }
  ];

  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Current Section Indicator */}
        <div className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <div className="flex items-center gap-2 text-red-300">
            <Star className="h-4 w-4" />
            <span className="text-sm font-medium">🃏 Tarot</span>
            <span className="text-red-500">/</span>
            <span className="text-sm">Okuma Teknikleri</span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="mb-6">
          <Link
            href="/about/tarot"
            className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tarot Ana Sayfası
          </Link>
        </div>

        {/* Header */}
        <div className="bg-gradient-to-r from-red-900/20 to-purple-900/20 rounded-xl p-8 mb-8 border border-red-500/20">
          <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
            <Brain className="h-10 w-10 text-red-400" />
            Tarot Okuma Teknikleri
          </h1>
          <p className="text-lg text-slate-200 leading-relaxed">
            Tarot okuma sanatını öğrenmek için temel spread'ler (yayılımlar) ve yorumlama tekniklerini keşfedin.
            Başlangıç seviyesinden ileri düzeye kadar farklı okuma yöntemleri ile tarot bilginizi derinleştirin.
          </p>
        </div>

        {/* Başlangıç Seviyeleri */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Başlangıç Seviyeleri</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {beginnerSpreads.map((spread) => (
              <Link
                key={spread.path}
                href={`/about/tarot/interpretation-techniques/${spread.path}`}
                className="group bg-slate-800 hover:bg-slate-700 rounded-xl p-6 border border-slate-700 hover:border-green-500/50 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    {spread.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 group-hover:text-green-300 transition-colors">
                      {spread.name}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-1 bg-green-500/20 text-green-300 rounded">
                        {spread.difficulty}
                      </span>
                      <span className="text-xs text-slate-400">
                        {spread.cards} kart
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mb-3">
                  {spread.description}
                </p>
                <div className="flex flex-wrap gap-1">
                  {spread.uses.map((use, index) => (
                    <span
                      key={index}
                      className="text-xs px-2 py-1 bg-slate-700 text-slate-400 rounded"
                    >
                      {use}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Orta Seviye */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Orta Seviye</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {intermediateSpreads.map((spread) => (
              <Link
                key={spread.path}
                href={`/about/tarot/interpretation-techniques/${spread.path}`}
                className="group bg-slate-800 hover:bg-slate-700 rounded-xl p-6 border border-slate-700 hover:border-yellow-500/50 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-yellow-500 to-orange-600 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    {spread.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 group-hover:text-yellow-300 transition-colors">
                      {spread.name}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-1 bg-yellow-500/20 text-yellow-300 rounded">
                        {spread.difficulty}
                      </span>
                      <span className="text-xs text-slate-400">
                        {spread.cards} kart
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mb-3">
                  {spread.description}
                </p>
                <div className="flex flex-wrap gap-1">
                  {spread.uses.map((use, index) => (
                    <span
                      key={index}
                      className="text-xs px-2 py-1 bg-slate-700 text-slate-400 rounded"
                    >
                      {use}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* İleri Seviye */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">İleri Seviye</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {advancedSpreads.map((spread) => (
              <Link
                key={spread.path}
                href={`/about/tarot/interpretation-techniques/${spread.path}`}
                className="group bg-slate-800 hover:bg-slate-700 rounded-xl p-6 border border-slate-700 hover:border-purple-500/50 transition-all duration-300"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-700 rounded-lg flex items-center justify-center group-hover:scale-110 transition-transform">
                    {spread.icon}
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 group-hover:text-purple-300 transition-colors">
                      {spread.name}
                    </h3>
                    <div className="flex items-center gap-2">
                      <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-300 rounded">
                        {spread.difficulty}
                      </span>
                      <span className="text-xs text-slate-400">
                        {spread.cards} kart
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-slate-300 text-sm mb-3">
                  {spread.description}
                </p>
                <div className="flex flex-wrap gap-1">
                  {spread.uses.map((use, index) => (
                    <span
                      key={index}
                      className="text-xs px-2 py-1 bg-slate-700 text-slate-400 rounded"
                    >
                      {use}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* Genel İpuçları */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold text-slate-100 mb-6">Başarılı Okuma İpuçları</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-blue-400" />
                Hazırlık Aşaması
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">Sakin ortam oluşturun</p>
                    <p className="text-slate-400 text-xs">Sessiz, huzurlu bir alan seçin</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">Niyetinizi netleştirin</p>
                    <p className="text-slate-400 text-xs">Hangi konuda rehberlik istediğinizi belirleyin</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">Kartları temizleyin</p>
                    <p className="text-slate-400 text-xs">Önceki enerjileri temizlemek için karıştırın</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Brain className="h-5 w-5 text-purple-400" />
                Yorumlama Süreci
              </h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">İlk izleniminize güvenin</p>
                    <p className="text-slate-400 text-xs">Kartı görünce hissettiğiniz ilk duyguyu not edin</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">Sembolleri inceleyin</p>
                    <p className="text-slate-400 text-xs">Renk, sayı ve figürlere dikkat edin</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 bg-purple-400 rounded-full mt-2 flex-shrink-0"></div>
                  <div>
                    <p className="text-slate-300 font-medium text-sm">Kart kombinasyonlarını değerlendirin</p>
                    <p className="text-slate-400 text-xs">Kartların birbirleriyle olan ilişkisini analiz edin</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Önemli Uyarı */}
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-200 mb-3 flex items-center gap-2">
            <Star className="h-5 w-5" />
            Önemli Uyarı
          </h3>
          <p className="text-amber-100 leading-relaxed">
            Tarot okuma teknikleri sembolik ve subjektif değerlendirme yöntemleridir. Kesin sonuçlar vermez ve bilimsel
            doğruluk iddiası taşımaz. İçerikler tamamen merak, eğlence ve kişisel farkındalık amaçlıdır. Önemli yaşam
            kararları için profesyonel uzman görüşü alınmalıdır.
          </p>
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Link
            href="/about/tarot"
            className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tarot Ana Sayfa
          </Link>

          <Link
            href="/about/tarot/interpretation-techniques/tek-kart"
            className="inline-flex items-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            İlk Tekniğe Başla
            <Star className="h-4 w-4 ml-2" />
          </Link>
        </div>
      </div>
    </AboutLayout>
  );
}